import { convexQuery } from "@convex-dev/react-query";
import type { Id } from "@convex/_generated/dataModel";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAction } from "convex/react";
import { AlertCircle, Plus, Settings2, ShieldAlert, Store } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  locationsGetAllMineWithTerminals,
  stripeTerminalListLocations,
  stripeTerminalListReaders,
} from "~/api-refs";
import { PageWrapper } from "~/components/page-wrapper";
import { LocationTerminalCard } from "~/components/terminals/location-terminal-card";
import { OrderHistory } from "~/components/terminals/order-history";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { Spinner } from "~/components/ui/spinner";
import { useEmployeePermissions } from "~/hooks/use-employee-permissions";
import { useActiveOrganization } from "~/integrations/vortex-auth/hooks";
import { logger } from "~/lib/logger";
import type {
  StripeTerminalLocation,
  StripeTerminalReader,
} from "~/lib/stripe-terminal";

const TERMINALS_PAGE_DESCRIPTION = "Manage card readers for your locations";

export const Route = createFileRoute("/_authenticated/settings/terminals")({
  component: TerminalsPage,
  pendingComponent: () => (
    <PageWrapper
      description={TERMINALS_PAGE_DESCRIPTION}
      title="Terminal Management"
    >
      <div className="flex items-center justify-center p-12">
        <Spinner className="size-8" />
      </div>
    </PageWrapper>
  ),
});

function TerminalsPage() {
  const navigate = useNavigate();
  const { organization } = useActiveOrganization();
  const {
    hasPermission,
    isLoading: permissionsLoading,
    hasEmployee,
  } = useEmployeePermissions();
  const canViewIntegrations = hasPermission("viewIntegrations");

  // Fetch locations with terminal info
  const { data: appLocationsData, isLoading: isLoadingLocations } = useQuery({
    ...convexQuery(locationsGetAllMineWithTerminals, {}),
    enabled: !!organization && canViewIntegrations,
  });
  const appLocations = useMemo(
    () => appLocationsData ?? [],
    [appLocationsData]
  );

  const listLocations = useAction(stripeTerminalListLocations);
  const listReaders = useAction(stripeTerminalListReaders);

  const [terminalLocations, setTerminalLocations] = useState<
    StripeTerminalLocation[]
  >([]);
  const [readers, setReaders] = useState<StripeTerminalReader[]>([]);
  const [isLoadingReaders, setIsLoadingReaders] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!organization) {
      return;
    }

    try {
      setIsLoadingReaders(true);
      setError(null);

      // Load locations to get display names
      const locationsData = await listLocations({});
      setTerminalLocations(locationsData);

      // Load readers
      const readersData = await listReaders({});
      setReaders(readersData);
    } catch (err) {
      logger.exception("terminal_data_load_failed", err);
      setError(
        err instanceof Error ? err.message : "Failed to load terminal data"
      );
    } finally {
      setIsLoadingReaders(false);
    }
  }, [organization, listLocations, listReaders]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // Get location info by ID
  const getTerminalLocation = (
    stripeTerminalLocationId: string
  ): StripeTerminalLocation | undefined => {
    return terminalLocations.find(
      (terminalLocation) => terminalLocation.id === stripeTerminalLocationId
    );
  };

  const getReadersForTerminalLocation = (
    stripeTerminalLocationId: string
  ): StripeTerminalReader[] => {
    return readers.filter(
      (reader) => reader.location === stripeTerminalLocationId
    );
  };

  // Locations that have terminal locations set up
  const locationsWithTerminals = appLocations.filter(
    (r) => r.stripeTerminalLocationId
  );

  // Locations that need terminal setup
  const locationsWithoutTerminals = appLocations.filter(
    (r) => !r.stripeTerminalLocationId
  );

  // V4.1: Route-based navigation for modals
  const navigateToSetup = (locationId: Id<"locations">) => {
    void navigate({
      to: "/settings/terminals/setup/$locationId",
      params: { locationId },
    });
  };

  const navigateToRegister = (locationId: string) => {
    void navigate({
      to: "/settings/terminals/register/$locationId",
      params: { locationId },
    });
  };

  const navigateToEditReader = (readerId: string, label?: string) => {
    void navigate({
      to: "/settings/terminals/readers/$readerId/edit",
      params: { readerId },
      search: { label: label ?? "" },
    });
  };

  const navigateToDeleteReader = (
    readerId: string,
    label?: string,
    deviceType?: string
  ) => {
    void navigate({
      to: "/settings/terminals/readers/$readerId/delete",
      params: { readerId },
      search: { label: label ?? "", deviceType: deviceType ?? "Reader" },
    });
  };

  const navigateToOrder = (locationId: Id<"locations">) => {
    void navigate({
      to: "/settings/terminals/order/$locationId",
      params: { locationId },
    });
  };

  if (!organization) {
    return null;
  }

  // Loading state for permissions
  if (permissionsLoading) {
    return (
      <PageWrapper
        description={TERMINALS_PAGE_DESCRIPTION}
        title="Terminal Management"
      >
        <Skeleton className="h-96 w-full" />
      </PageWrapper>
    );
  }

  // No employee record linked
  if (!hasEmployee) {
    return (
      <PageWrapper
        description={TERMINALS_PAGE_DESCRIPTION}
        title="Terminal Management"
      >
        <Alert>
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>No Employee Profile</AlertTitle>
          <AlertDescription>
            Your account is not linked to an employee profile. Contact your
            organization admin to set up your employee record with the
            appropriate permissions.
          </AlertDescription>
        </Alert>
      </PageWrapper>
    );
  }

  // No permission to view terminals
  if (!canViewIntegrations) {
    return (
      <PageWrapper
        description={TERMINALS_PAGE_DESCRIPTION}
        title="Terminal Management"
      >
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            You don't have permission to view terminal settings. Contact your
            manager to request access.
          </AlertDescription>
        </Alert>
      </PageWrapper>
    );
  }

  // Show loading state while fetching locations
  if (isLoadingLocations) {
    return (
      <PageWrapper
        description={TERMINALS_PAGE_DESCRIPTION}
        title="Terminal Management"
      >
        <div className="flex items-center justify-center p-12">
          <Spinner className="size-8" />
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper
      description={TERMINALS_PAGE_DESCRIPTION}
      title="Terminal Management"
    >
      <div className="flex flex-col gap-6">
        {/* Error Message */}
        {error && (
          <Card className="border-destructive">
            <CardContent className="flex items-center gap-3 p-4">
              <AlertCircle className="text-destructive size-5" />
              <div className="flex-1">
                <p className="subtitle-sm text-destructive">{error}</p>
                <p className="body-sm text-muted-foreground mt-1">
                  Make sure you've completed onboarding and have a Stripe
                  Connect account.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Locations Without Terminal Locations (legacy/edge case) */}
        {locationsWithoutTerminals.length > 0 && (
          <Card className="border-warning/30 bg-warning/5 dark:border-warning/30 dark:bg-warning/10">
            <CardHeader>
              <CardTitle className="text-warning dark:text-warning flex items-center gap-2">
                <Settings2 className="size-5" />
                Action Required
              </CardTitle>
              <CardDescription className="text-warning dark:text-warning">
                These locations need a Stripe Terminal Location before you can
                order or register card readers.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="divide-warning/30 dark:divide-warning/30 divide-y">
                {locationsWithoutTerminals.map((location) => (
                  <div
                    className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
                    key={location.id}
                  >
                    <div className="flex items-center gap-3">
                      <div className="bg-warning/10 dark:bg-warning/10 flex size-10 items-center justify-center rounded-lg">
                        <Store className="text-warning dark:text-warning size-5" />
                      </div>
                      <div>
                        <h4 className="subtitle-sm">{location.name}</h4>
                        {location.addressLine1 && (
                          <p className="body-sm text-muted-foreground">
                            {location.city}, {location.state}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() => navigateToSetup(location.id)}
                      size="sm"
                    >
                      <Plus className="mr-2 size-4" />
                      Set Up Terminal
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Locations with Terminals */}
        {locationsWithTerminals.length === 0 &&
          locationsWithoutTerminals.length === 0 && (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Store className="text-muted-foreground/30 mb-4 size-12" />
                <h3 className="subtitle-sm text-muted-foreground">
                  No Locations Found
                </h3>
                <p className="body-sm text-muted-foreground mt-1">
                  Create a location first, then add a Stripe Terminal Location.
                </p>
              </CardContent>
            </Card>
          )}

        {locationsWithTerminals.map((location) => {
          const locationInfo = location.stripeTerminalLocationId
            ? getTerminalLocation(location.stripeTerminalLocationId)
            : undefined;
          const locationReaders = location.stripeTerminalLocationId
            ? getReadersForTerminalLocation(location.stripeTerminalLocationId)
            : [];

          return (
            <LocationTerminalCard
              isLoadingReaders={isLoadingReaders}
              key={location.id}
              location={location}
              locationInfo={locationInfo}
              onDeleteReader={navigateToDeleteReader}
              onEditReader={navigateToEditReader}
              onOrder={navigateToOrder}
              onRegister={navigateToRegister}
              readers={locationReaders}
            />
          );
        })}

        {/* Order History */}
        <OrderHistory limit={10} />
      </div>
    </PageWrapper>
  );
}
